import { useMemo, useState } from 'react'
import {
  ApiError, api, createArchiveJob, fmtTime, qs,
  type ArchiveJob, type Availability, type Calibration, type Me,
  type StoredObject,
} from '../api'
import { useApi } from '../useApi'
import { autoRange, useSelection, useViewport, type View } from '../charts'
import {
  AvailabilityStrip, BandSpectrogram, LevelHistory, TimeAxis, ViewBar,
  type LevelEvent, type LevelMinute, type LevelPoint,
} from '../components/LevelsChart'
import { SelectionBar } from '../components/SelectionBar'
import { SelectionPlayer } from '../components/SelectionPlayer'
import { ArchiveJobs } from '../components/ArchiveJobs'
import { ClipPlayer } from '../components/ClipPlayer'
import { Empty, ErrorBox, Rows, Stat } from '../components/bits'

interface LevelsDoc {
  hours: number
  files: number
  calibration: Calibration
  bands_hz: number[]
  seconds_per_point: number
  points: LevelPoint[]
  minutes: LevelMinute[]
  events: LevelEvent[]
  coverage: { minutes: number; first: number | null; last: number | null }
}

const RANGES = [
  { h: 24, label: '24 hours' },
  { h: 72, label: '3 days' },
  { h: 168, label: '7 days' },
  { h: 720, label: '30 days' },
]

/** Audio is only worth chasing while it might still exist. The node keeps a
 *  bounded spool and `live/` expires within the day; three days is the span
 *  the operator asked for and roughly what a weekend needs. */
const LISTENABLE_H = 72

export function MissionLevels({
  missionId, me,
}: { missionId: string; me: Me }) {
  const [hours, setHours] = useState(72)
  const [open, setOpen] = useState<StoredObject | null>(null)
  const [lookup, setLookup] = useState<string | null>(null)

  const q = useApi<LevelsDoc>(
    `/v1/missions/${encodeURIComponent(missionId)}/levels?hours=${hours}`,
  )
  const d = q.data

  // Availability is a second request rather than a field on the levels
  // document, because the two answer different questions and fail
  // independently: the chart is still worth drawing when the node's inventory
  // is stale, and the strip is still worth drawing when a roll-up is missing.
  //
  // The URL is memoised and its bounds rounded to the hour, and both halves of
  // that matter. useApi keys its cache, its fetch callback and its effect on
  // the path string, so a URL built from Date.now() during render is a
  // different resource every render: it fetches, sets state, re-renders,
  // builds a new URL, and fetches again, without ever settling. Rounding is
  // what makes the key stable rather than merely tidy -- and the hour is the
  // right grain to round to because it is exactly what the endpoint buckets
  // at, so nothing is given up for it.
  const availUrl = useMemo(() => {
    const H = 3600_000
    const until = Math.ceil(Date.now() / H) * H
    return `/v1/missions/${encodeURIComponent(missionId)}/availability` + qs({
      // One hour of margin at each end, so the strip covers the whole span the
      // charts draw even as the current hour fills.
      since: new Date(until - (hours + 1) * H).toISOString(),
      until: new Date(until + H).toISOString(),
    })
  }, [missionId, hours])
  const avail = useApi<Availability>(availUrl)

  // Polled, unlike the rest of this page: a job's progress is the one thing
  // here that changes without the operator doing anything, and the answer to
  // "is it moving" loses its value if it needs a refresh to arrive.
  const jobs = useApi<{ items: ArchiveJob[] }>(
    `/v1/missions/${encodeURIComponent(missionId)}/archive-jobs`,
    { poll: 30_000 },
  )

  // One crosshair for both charts. Hovering the level line marks the same
  // instant on the spectrum above it, because the question is nearly always
  // "what did the frequencies do when the level did that".
  const [hoverT, setHoverT] = useState<number | null>(null)

  // Every hook runs before the early returns below, so loading a new range
  // does not change how many of them React sees.
  const full = useMemo<View>(() => ({
    t0: d?.minutes[0]?.t ?? 0,
    t1: d?.minutes[d.minutes.length - 1]?.t ?? 0,
  }), [d])
  const vp = useViewport(full)
  const sel = useSelection()
  const [listening, setListening] = useState<StoredObject[] | null>(null)
  const [pulling, setPulling] = useState(false)
  const [pullNote, setPullNote] = useState<string | null>(null)

  /** Collect what is stored across the span, both kinds, and open the player.
   *
   *  Both kinds in one request rather than choosing here: the player prefers a
   *  proxy where one exists and offers the original beside it, and that choice
   *  belongs where the listener is. */
  const listen = async (s: { t0: number; t1: number }) => {
    const since = new Date(s.t0 * 1000).toISOString()
    const until = new Date(s.t1 * 1000).toISOString()
    const found: StoredObject[] = []
    for (const kind of ['reduced', 'audio']) {
      const r = await api.get<{ items: StoredObject[] }>(
        `/v1/missions/${encodeURIComponent(missionId)}/objects`
        + qs({ kind, since, until, state: 'stored', limit: 500 }))
      found.push(...r.items)
    }
    setListening(found)
  }

  /** Ask the node for the marked range.
   *
   *  The server may trim the request to what the node still holds, so the
   *  response is read back and reported rather than assumed. A silently
   *  trimmed request is a request the operator still believes they made.
   */
  const askNode = async (s: { t0: number; t1: number }) => {
    setPulling(true)
    setPullNote(null)
    try {
      const r = await createArchiveJob(missionId, {
        mode: 'range',
        from_utc: new Date(s.t0 * 1000).toISOString(),
        to_utc: new Date(s.t1 * 1000).toISOString(),
      })
      setPullNote(
        r.horizon.clipped && r.from_utc
          ? `Asked for ${fmtTime(r.from_utc)} onward — the earlier part of your`
            + ' selection is no longer on the node.'
          : 'Asked. The node picks this up on its next check-in.',
      )
      jobs.reload()
      avail.reload()
    } catch (e) {
      setPullNote(e instanceof ApiError ? e.detail || e.code : 'Could not ask the node.')
    } finally {
      setPulling(false)
    }
  }

  /* Both scales come from the data. They used to be constants -- -140..-80
   * here, -95..-20 on the live view -- and on this hydrophone the median band
   * level is -140.8 dBFS, so half of every sample sat on the floor and the
   * spectrum looked empty apart from a couple of transients. A hydrophone in
   * air in a quiet room and one on a busy quay are 60 dB apart; no single pair
   * of constants is right for both, and the honest answer is to measure. */
  const bandRange = useMemo(
    () => autoRange(d ? d.points.flatMap((p) => p.bands) : []),
    [d],
  )
  const levelRange = useMemo(() => autoRange(
    d ? d.minutes.flatMap((m) =>
      [m.bb, m.l5, m.l95, m.peak].filter((v): v is number => v !== null)) : [],
  ), [d])

  /** Find audio recorded around an event.
   *
   *  The node decides whether to keep audio for a detection (`keep_audio`), so
   *  an event can legitimately exist with no recording behind it. Saying which
   *  of those two happened is the difference between a useful empty state and
   *  a broken-looking button.
   */
  const findAudio = async (e: LevelEvent) => {
    setLookup(String(e.t))
    setOpen(null)
    try {
      const since = new Date((e.t - 120) * 1000).toISOString()
      const until = new Date((e.t + (e.duration_s ?? 60) + 120) * 1000).toISOString()
      const found: StoredObject[] = []
      for (const kind of ['clips', 'audio', 'reference']) {
        const r = await api.get<{ items: StoredObject[] }>(
          `/v1/missions/${encodeURIComponent(missionId)}/objects${qs({ kind, since, until, state: 'stored', limit: 20 })}`,
        )
        found.push(...r.items)
      }
      if (!found.length) {
        setLookup(`none:${e.t}`)
        return
      }
      found.sort((a, b) =>
        Math.abs(new Date(a.ts).getTime() / 1000 - e.t) -
        Math.abs(new Date(b.ts).getTime() / 1000 - e.t))
      setOpen(found[0]!)
      setLookup(null)
    } catch {
      setLookup(`none:${e.t}`)
    }
  }

  if (q.cold) return <Rows n={6} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!d) return null

  const nowS = Date.now() / 1000

  return (
    <div className="stack">
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <div className="seg" role="tablist" aria-label="Time range">
          {RANGES.map((r) => (
            <button key={r.h} role="tab" aria-selected={r.h === hours}
              onClick={() => setHours(r.h)}>
              {r.label}
            </button>
          ))}
        </div>

        {/* Shift-drag does this on a desktop, but there is no shift key on a
            phone and this app is used on a quay. The toggle is the only way
            the gesture is reachable there, so it is the primary control and
            the modifier is the shortcut. */}
        <div className="seg" role="tablist" aria-label="Pointer mode">
          <button role="tab" aria-selected={sel.mode === 'pan'}
            onClick={() => { sel.setMode('pan'); sel.clear() }}>
            Pan
          </button>
          <button role="tab" aria-selected={sel.mode === 'select'}
            onClick={() => sel.setMode('select')}>
            Select
          </button>
        </div>
      </div>

      {d.minutes.length === 0 ? (
        <div className="card flush">
          <Empty title="Nothing recorded in this window">
            The node uploads an hourly roll-up of levels. Either it has not sent
            one for this period, or it was not recording.
          </Empty>
        </div>
      ) : (
        <>
          <div className="grid c4">
            <div className="card"><Stat label="Minutes recorded" value={d.coverage.minutes.toLocaleString()} small /></div>
            <div className="card"><Stat label="Events" value={d.events.length.toLocaleString()} small /></div>
            <div className="card">
              <Stat label="Median level" value={median(d.minutes)} small
                title="L50 across the window" />
            </div>
            <div className="card"><Stat label="Loudest" value={loudest(d.minutes)} small /></div>
          </div>

          <div className="card flush">
            <div className="card-head">
              <h2>Frequency</h2>
              <span className="faint" style={{ fontSize: 13 }}>
                {d.seconds_per_point > 1 ? `${d.seconds_per_point}s per column` : 'per second'}
              </span>
            </div>
            <div className="card-body stack">
              <BandSpectrogram
                points={d.points} bandsHz={d.bands_hz}
                vp={vp} range={bandRange} sel={sel}
                hoverT={hoverT} setHoverT={setHoverT}
              />

              <div className="card-head" style={{ padding: 0, border: 0 }}>
                <h2>Level</h2>
                <span className="faint" style={{ fontSize: 13 }}>
                  line is broadband, dashed is L50, shaded band is L5–L95
                </span>
              </div>
              <LevelHistory
                minutes={d.minutes} events={d.events}
                vp={vp} range={levelRange} sel={sel}
                hoverT={hoverT} setHoverT={setHoverT}
                onPickEvent={(e) => void findAudio(e)}
              />

              {/* Between the charts and the axis, sharing the viewport, so
                  "what the sea did" and "whether I can still hear it" are one
                  picture read top to bottom. */}
              {avail.data && (
                <>
                  <div className="card-head" style={{ padding: 0, border: 0 }}>
                    <h2>Audio</h2>
                    <span className="faint" style={{ fontSize: 13 }}>
                      where each hour is held
                    </span>
                  </div>
                  <AvailabilityStrip
                    hours={avail.data.hours} node={avail.data.node}
                    vp={vp} sel={sel}
                  />
                </>
              )}

              <TimeAxis view={vp.view} />
              <ViewBar vp={vp} full={full} />

              <div className="spec-foot">
                <span className="scale-bar" role="img" aria-label="quiet to loud" />
                {/* The numbers, because the scale is now measured rather than
                    assumed -- a colour whose meaning is not stated is a colour
                    that cannot be compared with anything. */}
                <span className="num">{bandRange[0]} to {bandRange[1]} dB</span>
                <span>{d.calibration.basis}</span>
                {!d.calibration.absolute && (
                  <span className="badge" title={`Missing: ${d.calibration.missing.join(', ')}`}>
                    not calibrated
                  </span>
                )}
                <span className="faint">{d.files} roll-up files read</span>
              </div>
            </div>
          </div>

          {sel.sel && avail.data && (
            /* onListen and onSave are deliberately still not passed: the
               multi-segment player and the batch download are not built, and
               an omitted handler makes the button say "not built yet" rather
               than look ready and do nothing when pressed. */
            <SelectionBar
              sel={sel.sel} hours={avail.data.hours} node={avail.data.node}
              canWrite={me.can_write} onClear={sel.clear}
              onListen={() => void listen(sel.sel!)}
              onPull={() => void askNode(sel.sel!)}
              pullBusy={pulling}
              pullNote={pullNote}
            />
          )}

          {listening && sel.sel && (
            <SelectionPlayer
              missionId={missionId} objects={listening}
              segmentSeconds={avail.data?.segment_seconds ?? 60}
              t0={sel.sel.t0} t1={sel.sel.t1}
              onClose={() => setListening(null)} />
          )}

          {jobs.data && (
            <ArchiveJobs missionId={missionId} jobs={jobs.data.items}
              canWrite={me.can_write} onChanged={jobs.reload} />
          )}

          {open && (
            <ClipPlayer object={open} calibration={d.calibration}
              onClose={() => setOpen(null)} />
          )}

          <div className="card flush">
            <div className="card-head">
              <h2>Events</h2>
              <span className="faint" style={{ fontSize: 13 }}>
                classified by the node, not by this server
              </span>
            </div>
            {d.events.length === 0 ? (
              <Empty title="Nothing stood out">
                Every minute in this window was classified as background.
              </Empty>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th><th>Kind</th><th className="r">Score</th>
                    <th>Frequencies</th><th className="r">Peak</th><th>Audio</th>
                  </tr>
                </thead>
                <tbody>
                  {d.events.slice(0, 200).map((e) => {
                    const fresh = nowS - e.t < LISTENABLE_H * 3600
                    const none = lookup === `none:${e.t}`
                    return (
                      <tr key={e.t}>
                        <td data-l="When" className="num">{fmtTime(new Date(e.t * 1000).toISOString())}</td>
                        <td data-l="Kind">
                          <span className={`badge${e.verdict === 'event' ? ' alert' : ''}`}>
                            {e.verdict}
                          </span>
                        </td>
                        <td data-l="Score" className="r num">{e.score?.toFixed(1) ?? '—'}</td>
                        <td data-l="Frequencies" className="num faint" title={e.reason ?? ''}>
                          {e.drivers_hz.length
                            ? `${Math.round(Math.min(...e.drivers_hz))}–${Math.round(Math.max(...e.drivers_hz))} Hz`
                            : '—'}
                        </td>
                        <td data-l="Peak" className="r num">
                          {e.peak_db != null ? `${e.peak_db.toFixed(1)} dB` : '—'}
                        </td>
                        <td data-l="Audio">
                          {none ? (
                            <span className="faint" title={
                              e.keep_audio
                                ? 'The node decided to keep this audio but has not uploaded it. Its tier may not include clips.'
                                : 'The node did not keep audio for this detection.'}>
                              {e.keep_audio ? 'kept on node' : 'not kept'}
                            </span>
                          ) : !fresh ? (
                            <span className="faint" title={`Older than ${LISTENABLE_H} hours`}>expired</span>
                          ) : (
                            <button className="btn sm" disabled={lookup === String(e.t)}
                              onClick={() => void findAudio(e)}>
                              {lookup === String(e.t) ? 'Looking…' : 'Listen'}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          <p className="faint" style={{ fontSize: 13, maxWidth: '74ch' }}>
            Levels and classifications are the node's own, computed on the Pi from
            audio that never left it. This server reads the hourly roll-up and
            draws it; it does not re-analyse anything.
            {me.can_write && ' '}
          </p>
        </>
      )}
    </div>
  )
}

function median(ms: LevelMinute[]): string {
  const v = ms.map((m) => m.l50).filter((x): x is number => x !== null).sort((a, b) => a - b)
  if (!v.length) return '—'
  return `${v[Math.floor(v.length / 2)]!.toFixed(0)} dB`
}

/* The loudest minute, and whether that is a number or a floor.
 *
 * This read "0 dB" for eleven days, on a fleet whose converter was railing on
 * 166 minutes across 11 of 16 days, and nobody looked at it twice. The node's
 * report put that down to the field being unreachable; it was not unreachable,
 * it was displayed right here and unremarked -- which is worse, and is why the
 * fix is not "surface the field" but "a value sitting exactly on a physical
 * limit is not a value".
 *
 * `0 dBFS` is the converter against its stop. The true peak is somewhere above
 * it and there is no way to find out how far, so the honest rendering is a
 * bound. */
function loudest(ms: LevelMinute[]): string {
  const v = ms.filter((m) => m.peak !== null)
  if (!v.length) return '—'
  const top = v.reduce((a, b) => (b.peak! > a.peak! ? b : a))
  return `${top.at_least ? '≥ ' : ''}${top.peak!.toFixed(0)} dB`
}
